import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { isSingleRaceTeam } from "../../config/competitionConfig";
import "../styles/SelectCyclistsBonus.css";

const SelectCyclistsBonus = ({
  selectedCyclists,
  setCyclistsBonus,
  cyclistsBonus,
  nextStep,
  prevStep,
  teamId,
  saveData,
}) => {
  const { t } = useTranslation();

  const [confirmValidDataPopup, setConfirmValidDataPopup] = useState(false);
  const [saveError, setSaveError] = useState("");
  const navigate = useNavigate();
  const isSingleRace = isSingleRaceTeam(teamId);

  const handleCyclistsBonus = (cyclist) => {
    if (cyclistsBonus.some((c) => c.cyclistId === cyclist.cyclistId)) {
      setCyclistsBonus(cyclistsBonus.filter((c) => c.cyclistId !== cyclist.cyclistId));
    } else {
      setCyclistsBonus([...(cyclistsBonus || []), cyclist]);
    }
  };

  const isCyclistsBonusSelected = (cyclistId) => {
    return cyclistsBonus.some((c) => c.cyclistId === cyclistId);
  };

  const validData = async () => {
    setSaveError("");

    try {
      await saveData();
      setConfirmValidDataPopup(true);
    } catch (error) {
      console.error("Erreur lors de l'enregistrement :", error);
      setSaveError(error?.message || t("selectCyclistsBonus.errors.saveFailed"));
    }
  };

  const handleClosePopup = () => {
    navigate("/profil");
  };

  const flagSrc = (nationality) =>
    `${process.env.PUBLIC_URL}/flags/${(nationality || "")
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/ /g, "_")
      .toLowerCase()}.png`;

  return (
    <main className="select-cyclists-bonus">
      {confirmValidDataPopup && (
        <div className="select-cyclists-bonus-popup">
          <div className="select-cyclists-bonus-popup-content">
            <p>
              {t("selectCyclistsBonus.popupTextLine1")}
              <br />
              {t("selectCyclistsBonus.popupTextLine2")}
            </p>
            <button onClick={handleClosePopup}>{t("common.confirm")}</button>
          </div>
        </div>
      )}

      <div className="select-cyclists-bonus-shadow-container">
        <div className="select-cyclists-bonus-title">
          <h1>{t("selectCyclistsBonus.title")}</h1>
          <div className="select-cyclists-bonus-shadow-mask-right"></div>
          <div className="select-cyclists-bonus-shadow-mask-bottom"></div>
        </div>
      </div>

      <div className="select-cyclists-bonus-header">
        <div className="select-cyclists-bonus-rules">
          <p>{t("selectCyclistsBonus.rules")}</p>
          {saveError && (
            <p className="select-cyclists-bonus-save-error" role="alert">
              {saveError}
            </p>
          )}
        </div>

        <div className="select-cyclists-bonus-change-step">
          <button onClick={prevStep}>{t("common.previous")}</button>
          <button
            onClick={teamId === 1 ? nextStep : validData}
            disabled={isSingleRace ? cyclistsBonus.length !== 1 : cyclistsBonus.length > 1}
          >
            {t("common.next")}
          </button>
        </div>
      </div>

      <div className="select-cyclists-bonus-pannel-view">
        <div className="select-cyclists-bonus-choice">
          <div className="select-cyclists-bonus-table-container">
            <table>
              <thead>
                <tr>
                  <th>{t("selectCyclistsBonus.table.cyclist")}</th>
                  <th>{t("selectCyclistsBonus.table.team")}</th>
                  <th>{t("selectCyclistsBonus.table.nation")}</th>
                  <th>{t("selectCyclistsBonus.table.value")}</th>
                  <th>{t("selectCyclistsBonus.table.speciality")}</th>
                </tr>
              </thead>

              <tbody>
                {selectedCyclists
                  .filter((c) => c.finalValue < 5)
                  .map((cyclist) => (
                    <tr
                      key={cyclist.cyclistId}
                      onClick={() => handleCyclistsBonus(cyclist)}
                      className={
                        isCyclistsBonusSelected(cyclist.cyclistId)
                          ? "select-cyclists-bonus-table-selected-row"
                          : ""
                      }
                    >
                      <td>
                        <div className="select-cyclists-bonus-name-info">
                          <img
                            className="select-cyclists-flag-icon"
                            src={flagSrc(cyclist.nationality)}
                            alt={t("selectCyclistsBonus.flagAlt", {
                              nationality: cyclist.nationality,
                            })}
                            width="20"
                            height="20"
                            onError={(e) => {
                              e.currentTarget.style.visibility = "hidden";
                            }}
                          />
                          <span>{cyclist.name}</span>
                        </div>
                      </td>
                      <td>{cyclist.team}</td>
                      <td>{cyclist.nationality}</td>
                      <td>{cyclist.finalValue}</td>
                      <td>{cyclist.speciality}</td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="select-cyclists-bonus-line-container">
          {cyclistsBonus.map((cyclist) => (
            <div className="select-cyclists-bonus-selected-cell" key={cyclist.cyclistId}>
              <div className="select-cyclists-bonus-selected-info">
                <img
                  src={`${process.env.PUBLIC_URL}/icons/cancel.png`}
                  className="select-cyclists-bonus-cancel-icon"
                  alt={t("selectCyclistsBonus.removeAlt")}
                  width="17"
                  height="17"
                  onClick={() => handleCyclistsBonus(cyclist)}
                />
                <h4>
                  <img
                    className="select-cyclists-bonus-flag-icon"
                    src={flagSrc(cyclist.nationality)}
                    alt={t("selectCyclistsBonus.flagAlt", {
                      nationality: cyclist.nationality,
                    })}
                    width="20"
                    height="20"
                    onError={(e) => {
                      e.currentTarget.style.visibility = "hidden";
                    }}
                  />
                  {cyclist.name}
                </h4>
                <p>{cyclist.team}</p>
                <p>
                  {t("selectCyclistsBonus.valueLabel")} :{" "}
                  <strong>{cyclist.finalValue}</strong>
                </p>
                <div
                  style={{
                    width: "100%",
                    height: "15px",
                    backgroundColor: cyclist.teamColor,
                    marginTop: "35px",
                    marginLeft: "auto",
                    marginRight: "auto",
                  }}
                ></div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </main>
  );
};

export default SelectCyclistsBonus;
